/**
 * Matching a drawn province to a city in the database.
 *
 * <p>There is no id to join on. The shapes come from a public-domain boundary file that
 * knows nothing about this product, and the cities were typed into the geography screen.
 * The name is the only thing the two have in common — which is workable, because a Turkish
 * province is the one piece of geography whose name does not change.</p>
 *
 * <p>What does change is how it is spelled. Natural Earth writes Elâzığ and Hakkâri with a
 * circumflex; the cities here are Elazığ and Hakkari. The generator corrects the two it
 * knows about, and this folds the rest, so the next release of the boundary file deciding
 * to write Kırşehir differently cannot leave a hole in the map with nothing saying why.</p>
 */

/**
 * A province name reduced to what two spellings of it have in common.
 *
 * <p>Turkish-aware lower-casing first, because `'İ'.toLowerCase()` in the invariant culture
 * leaves a combining dot behind and "İstanbul" would then fold differently from "istanbul".
 * Then the accents, and then the letters themselves: a customer who types "Mugla" with no
 * umlaut still gets their store on the map.</p>
 */
export function fold(name: string): string {
  return name
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    // Every combining mark, which covers the circumflex as well as the dot NFD pulls off İ.
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim();
}
